import type { PaginatedData } from '@/shared/types';

export type FilterFieldType = 'TEXT' | 'NUMBER' | 'DATE' | 'DATETIME' | 'ENUM' | 'BOOLEAN';
export type FilterOperator =
  | 'CONTAINS'
  | 'EQUALS'
  | 'STARTS_WITH'
  | 'ENDS_WITH'
  | 'GT'
  | 'GTE'
  | 'LT'
  | 'LTE'
  | 'BETWEEN'
  | 'BEFORE'
  | 'AFTER'
  | 'LAST_DAYS'
  | 'NOT_EQUALS'
  | 'IN'
  | 'NOT_IN';

export type SortDirection = 'ASC' | 'DESC';

export type DynamicFilterCondition = {
  field: string;
  operator: FilterOperator;
  value: unknown;
};

export type DynamicFilterGroup = {
  logic: 'AND';
  conditions: DynamicFilterCondition[];
};

export type AdminSearchRequest = {
  page: number;
  size: number;
  sort: Array<{ field: string; direction: SortDirection }>;
  filters?: DynamicFilterGroup;
};

export type AdminFilterPreset = {
  id: number;
  resource: AdminFilterResource;
  name: string;
  filters: DynamicFilterGroup;
  isDefault: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export type SaveAdminFilterPresetRequest = {
  name: string;
  filters: DynamicFilterGroup;
  isDefault?: boolean;
};

export type UpdateAdminFilterPresetRequest = Partial<SaveAdminFilterPresetRequest>;

export type FilterFieldMetadata = {
  key: string;
  label: string;
  type: FilterFieldType;
  operators: FilterOperator[];
  sortable: boolean;
  options?: Array<{ label: string; value: string | number | boolean }>;
};

export type FilterMetadataResponse = {
  fields: FilterFieldMetadata[];
  limits: {
    maxPageSize: number;
    maxConditions: number;
    maxDepth: number;
  };
  defaultSort: Array<{ field: string; direction: SortDirection }>;
};

export type AdminSearchResult<T> = PaginatedData<T>;

export const ADMIN_FILTER_RESOURCES = {
  users: 'users',
  roomAds: 'room-ads',
  roomWantedAds: 'room-wanted-ads',
  notifications: 'notifications',
  chatRooms: 'chat-rooms',
  chatMessages: 'chat-messages',
  countries: 'countries',
  regions: 'regions',
  cities: 'cities',
  districts: 'districts',
  nationalities: 'nationalities',
  housingStatuses: 'housing-statuses',
  propertyAmenities: 'property-amenities',
  referrals: 'referrals',
  systemSettings: 'system-settings',
  blockedUsers: 'blocked-users',
} as const;

export type AdminFilterResource = typeof ADMIN_FILTER_RESOURCES[keyof typeof ADMIN_FILTER_RESOURCES];

export type DynamicRelationLookup = {
  fetchFn: (params: { search: string; page: number; size: number }) => Promise<{
    content: any[];
    totalPages: number;
    totalItems: number;
    currentPage: number;
  }>;
  fetchById?: (id: string | number) => Promise<any>;
  mapOption: (item: any) => { value: string | number; label: string };
  placeholder?: string;
};

export type DynamicRelationLookups = Record<string, DynamicRelationLookup>;
